import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { router } from "./app/router";
import { QueryProvider } from "./query/QueryProvider";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { setLocale, SUPPORTED_LOCALES } from "./i18n/config";
import type { Locale } from "./i18n/config";
import "./i18n/config"; // side-effect: initialize i18next

/**
 * App root. Providers wrap the router so every route can use the query/mutation
 * hooks and the auth context:
 *   QueryProvider (TanStack Query) -> AuthProvider (GET /api/auth/me) -> Router.
 * The dashboard is the index route inside the protected /app shell and reads
 * the REAL API; live-sync (in AppShell) keeps it fresh across devices.
 */
export function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <LocaleSync />
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryProvider>
  );
}

/**
 * Once the trainer is known, apply their stored locale (unless they've already
 * picked one this session — handled by setLocale persisting to localStorage and
 * config seeding from it; this only seeds from the profile on first load).
 */
function LocaleSync() {
  const { trainer } = useAuth();
  useEffect(() => {
    const loc = trainer?.locale;
    if (!loc) return;
    if (typeof window !== "undefined" && window.localStorage.getItem("vf.locale")) {
      return; // user override wins
    }
    if ((SUPPORTED_LOCALES as readonly string[]).includes(loc)) {
      setLocale(loc as Locale);
    }
  }, [trainer?.locale]);
  return null;
}
